import React, { FC, useEffect, useState } from 'react'
import VendNumber from 'vend-number'
import { ApiRegister, ApiTransaction } from '@vend/types'
import { useT } from '@transifex/react'
import { Header, LoaderSpinner } from '@lightspeed/design-system-react'
import { useCnpPmofSessionEffectReducer } from '../../hooks/use-cnp-pmof-effect-reducer/useCnpPmofSessionEffectReducer'
import { ErrorContent } from '../modal-states/ErrorContent'
import { Loading } from '../modal-states/Loading'
import { SwiperError } from '../../helpers/lspay.types'
import { LSPAYModalContent } from '../modal-states/LSPAYModalContent'
import { LSPAYCnpPmofPaymentForm } from './LSPAYCnpPmofPaymentForm'

type Props = {
  amount: VendNumber
  customerId?: string
  outletId: string
  register: ApiRegister
  registerSaleId: string
  registerSalePaymentId: string
  retailerPaymentTypeId: string
  onError: () => void
  onClose: () => void
  onSuccess: (transaction: Promise<ApiTransaction>) => void
  setSwiperError: (swiperError: SwiperError) => void
  setDisableTerminalPayment: (disableTerminalPayment: boolean) => void
  setDismissible?: (dismissible: boolean) => void
  disableTerminalMonitoring?: boolean
  cancelProcessing?: () => void
}

export const LSPAYCnpPmofPayment: FC<Props> = ({
  amount,
  customerId,
  outletId,
  register,
  registerSaleId,
  registerSalePaymentId,
  retailerPaymentTypeId,
  setSwiperError,
  setDisableTerminalPayment,
  setDismissible,
  disableTerminalMonitoring,
  onError,
  onClose,
  onSuccess,
  cancelProcessing,
}) => {
  const [state, dispatch] = useCnpPmofSessionEffectReducer({
    amount,
    register,
    customerId,
    registerSalePaymentId,
    registerSaleId,
    retailerPaymentTypeId,
    outletId,
    setSwiperError,
    disableTerminalMonitoring,
  })

  const t = useT()
  const [isPaymentFormProcessing, setIsPaymentFormProcessing] = useState(false)

  useEffect(() => {
    if (state.status === 'done') {
      onSuccess(Promise.resolve(state.transaction))
    }
  }, [onSuccess, state])

  useEffect(() => {
    setDisableTerminalPayment(
      state.status === 'confirmingPaymentState' ||
        (isPaymentFormProcessing && state.status === 'collectingCardDetails')
    )
  }, [state, isPaymentFormProcessing, setDisableTerminalPayment])

  useEffect(() => {
    if (!setDismissible) return

    setDismissible(
      (state.status === 'collectingCardDetails' && !isPaymentFormProcessing) ||
        state.status === 'confirmingPaymentStateTimeout' ||
        state.status === 'error'
    )
  }, [isPaymentFormProcessing, setDismissible, state.status])

  // this will cancel the payment that was sent to the terminal before the user switched tabs to the manual entry form
  useEffect(() => {
    if (cancelProcessing) {
      cancelProcessing()
    }
  }, [cancelProcessing])

  const onPaymentFormProcessingStateChange = (isSubmitting: boolean) => {
    setIsPaymentFormProcessing(isSubmitting)
  }

  const onPaymentFormComplete = () => {
    dispatch({ type: 'FORM_SUBMISSION_COMPLETE' })
  }

  switch (state.status) {
    case 'creatingSession':
      return <Loading />
    case 'collectingCardDetails':
      return (
        <LSPAYCnpPmofPaymentForm
          session={state.session}
          onError={message =>
            dispatch({
              type: 'ERROR',
              errorMessage: { header: 'Error', body: message },
            })
          }
          onClose={onClose}
          onVerifyingPaymentMethodStateChange={
            onPaymentFormProcessingStateChange
          }
          onComplete={onPaymentFormComplete}
          isMotoWithSavePayment={!!customerId}
        />
      )
    case 'confirmingPaymentState':
      return (
        <LSPAYModalContent>
          <LoaderSpinner />
          <Header type="sub-heading" level="2">
            {t('Processing payment', {
              _context: 'Processing payment step header',
            })}
          </Header>
        </LSPAYModalContent>
      )
    case 'confirmingPaymentStateTimeout':
      return (
        <ErrorContent
          headingType="sub-heading"
          lspayError={{
            header: 'Timeout',
            body: t(
              'Confirming the payment state is taking longer than expected. Retry to continue waiting or choose a different payment method.'
            ),
          }}
          retry={() => {
            dispatch({ type: 'RETRY_CONFIRMING' })
          }}
          close={onError}
        />
      )
    case 'error':
      return (
        <ErrorContent
          headingType="sub-heading"
          lspayError={state.errorMessage}
          retry={() => {
            dispatch({ type: 'RESTART_WORKFLOW' })
          }}
          close={onError}
        />
      )
    case 'done': {
      // dialog should be closed
      return null
    }
    default: {
      // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
      const _exhaustiveCheck: never = state
      return _exhaustiveCheck
    }
  }
}
